import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import type { AnimationId, AntennaId } from '@wwf/sdk';

export type EditCellState = {
    antennaId: AntennaId;
    animationId: AnimationId;
    antennaName: string;
    animationName: string;
    count: number;
    inventoryComment: string | null;
    noSpecificMaterialRequired: boolean;
};

export type AnimationInventorySaveValues = {
    count: number;
    inventoryComment: string | null;
};

type AnimationInventoryEditCellDialogProps = {
    cell: EditCellState | null;
    onClose: () => void;
    onSave: (values: AnimationInventorySaveValues) => void;
    isSavePending: boolean;
};

export function AnimationInventoryEditCellDialog({
    cell,
    onClose,
    onSave,
    isSavePending,
}: AnimationInventoryEditCellDialogProps) {
    const notify = useNotify();
    const [draftCount, setDraftCount] = useState('');
    const [draftComment, setDraftComment] = useState('');

    useEffect(() => {
        if (!cell) return;
        setDraftCount(String(cell.count));
        setDraftComment(cell.inventoryComment ?? '');
    }, [cell]);

    const handleSave = () => {
        if (!cell) return;
        const parsed = Number.parseInt(draftCount, 10);
        const count = cell.noSpecificMaterialRequired ? cell.count : parsed;
        if (!cell.noSpecificMaterialRequired) {
            if (!Number.isFinite(parsed) || parsed < 0) {
                notify('Enter a non-negative whole number', {
                    type: 'warning',
                });
                return;
            }
        }
        const trimmed = draftComment.trim();
        const inventoryComment =
            trimmed.length > 0 ? trimmed.slice(0, 2000) : null;
        onSave({ count, inventoryComment });
    };

    return (
        <Dialog open={cell != null} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Edit inventory</DialogTitle>
            <DialogContent>
                {cell && (
                    <Box sx={{ pt: 1 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                        >
                            Animation
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1.5 }}
                        >
                            {cell.animationName}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                        >
                            Antenna
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            {cell.antennaName}
                        </Typography>
                        <TextField
                            label="Count"
                            type="number"
                            fullWidth
                            autoFocus={!cell.noSpecificMaterialRequired}
                            disabled={cell.noSpecificMaterialRequired}
                            value={draftCount}
                            onChange={e => setDraftCount(e.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                            helperText={
                                cell.noSpecificMaterialRequired
                                    ? 'No specific material required — count cannot be edited.'
                                    : undefined
                            }
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSave();
                                }
                            }}
                        />
                        <TextField
                            label="Comment"
                            fullWidth
                            multiline
                            minRows={2}
                            autoFocus={cell.noSpecificMaterialRequired}
                            value={draftComment}
                            onChange={e => setDraftComment(e.target.value)}
                            placeholder="Optional note for this antenna and animation"
                            inputProps={{ maxLength: 2000 }}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSavePending}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
